import { approvalMatrixApi } from '@/main/api';
import Button from '@/main/components/shared/button/button';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import ContainerScreen from '@/main/components/shared/containerScreen/containerLayout';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';
import {
  ApprovalMatrixSaveRequest,
  ApprovalMatrixValuesRequest
} from '@/main/models/request';
import { ApprovalMatrixDetailsResponse } from '@/main/models/response';
import { RoutesConstants } from '@/main/routes/routesConstants';
import ApprovalMatrixHistoric from '@/main/screens/registration/approvalMatrix/registerApprovalMatrix/approvalMatrixHistoric/approvalMatrixHistoric';
import ApprovalMatrixInfo from '@/main/screens/registration/approvalMatrix/registerApprovalMatrix/approvalMatrixInfo/approvalMatrixInfo';
import ApprovalMatrixValues from '@/main/screens/registration/approvalMatrix/registerApprovalMatrix/approvalMatrixValues/approvalMatrixValues';
import {
  approvalMatrixRegisterSchema,
  IApprovalMatrixRegisterForm,
  IApprovalMatrixRegisterFormOutput,
  INITIAL_APPROVAL_MATRIX_REGISTER_FORM
} from '@/main/screens/registration/approvalMatrix/registerApprovalMatrix/utilsRegisterApprovalMatrix';
import { getOptionsStatus } from '@/main/services/data/data';
import { SystemApplicationEnum } from '@/main/services/enum/SystemApplicationEnum';
import {
  getTranslateByKey,
  isNullUndefined,
  isNullUndefinedEmpty
} from '@/main/services/utils';
import { currencyToNumber, formatCurrency } from '@/main/services/utilsNumber';
import {
  getApprovalMatrixType,
  useApprovalMatrixTypeReducer
} from '@/main/store/approvalMatrixType';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import { getInsurers, useInsurerReducer } from '@/main/store/insurer';
import {
  getUserHierarchy,
  resetReducerUserHierarchy,
  useUserHierarchyReducer
} from '@/main/store/userHierarchy';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as S from './styles';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';

export default function RegisterApprovalMatrix() {
  const [searchParams] = useSearchParams();
  const approvalMatrixGuid = searchParams.get('guid');

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(!!approvalMatrixGuid);
  const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
  const [triggerReloadHistoric, setTriggerReloadHistoric] =
    useState<boolean>(false);
  const [approvalMatrixData, setApprovalMatrixData] =
    useState<ApprovalMatrixDetailsResponse | null>(null);

  const dispatch = useDispatch();
  const {
    countriesUser: { data: countries, isLoading: isLoadingCountries }
  } = useCountryReducer();
  const {
    insurers: { data: insurers, isLoading: isLoadingInsurers }
  } = useInsurerReducer();
  const {
    approvalMatrixType: { data: approvalMatrixTypes, isLoading: isLoadingTypes }
  } = useApprovalMatrixTypeReducer();
  const {
    userHierarchy: { data: userHierarchies, isLoading: isLoadingHierarchy }
  } = useUserHierarchyReducer();

  const methods = useForm<
    IApprovalMatrixRegisterForm,
    any,
    IApprovalMatrixRegisterFormOutput
  >({
    mode: 'onChange',
    defaultValues: INITIAL_APPROVAL_MATRIX_REGISTER_FORM,
    resolver: zodResolver(approvalMatrixRegisterSchema)
  });

  function handleBack() {
    navigate(RoutesConstants.registrationSearchApprovalMatrix);
  }

  function onSubmit(data: IApprovalMatrixRegisterFormOutput) {
    if (data.approvalMatrixValues.filter(item => item.active).length === 0) {
      toast.error(getTranslateByKey('information.atLeastOneApprovalValue'));
      return;
    }

    const request: ApprovalMatrixSaveRequest = {
      guid: isNullUndefinedEmpty(approvalMatrixGuid)
        ? undefined
        : approvalMatrixGuid,
      guidInsurer: String(data.approvalMatrixInformation.insurer.key),
      idApprovalMatrixType: Number(
        data.approvalMatrixInformation.approvalMatrixType.key
      ),
      idCountry: Number(data.approvalMatrixInformation.country.enum),
      idStatusRegister: Number(
        data.approvalMatrixInformation.statusRegister.key
      ),
      name: data.approvalMatrixInformation.matrixName,
      values: data.approvalMatrixValues.map(
        (val): ApprovalMatrixValuesRequest => ({
          active: val.active,
          guid: isNullUndefined(val.guid) ? undefined : val.guid,
          level: Number(val.hierarchyLevel.key),
          maximumValue: currencyToNumber(val.maximumValue) ?? 0
        })
      )
    };

    setIsLoadingSave(true);
    approvalMatrixApi
      .save(request)
      .then(res => {
        const { result } = res.data;
        if (result) {
          if (isNullUndefinedEmpty(approvalMatrixGuid)) {
            toast.success(getTranslateByKey("information.approvalMatrixCreateSuccess"))
            navigate(
              `${RoutesConstants.registrationRegisterApprovalMatrix}?guid=${result.guid}`
            );
          } else {
            toast.success(getTranslateByKey("information.approvalMatrixEditSuccess"))
            setTriggerReloadHistoric(prev => !prev);
          }
          methods.setValue(
            'approvalMatrixValues',
            result.approvalMatrixValues.map(val => ({
              hierarchyLevel:
                userHierarchies?.find(
                  h => Number(h.key) === val.hierarchyLevel
                ) || null,
              maximumValue: formatCurrency(val.maximumValue),
              active: val.active,
              guid: val.guid
            }))
          );
        }
      })
      .finally(() => {
        setIsLoadingSave(false);
      });
  }

  function load(guid: string) {
    dispatch(resetReducerUserHierarchy());
    setIsLoading(true);
    approvalMatrixApi.getDetails(guid).then(res => {
      const { result } = res.data;
      if (result) {
        setApprovalMatrixData(result);
        dispatch(
          getInsurers({
            guidCountry: String(result.guidCountry)
          })
        );
        dispatch(
          getUserHierarchy(result.guidInsurer, SystemApplicationEnum.Parts)
        );
      }
    });
  }

  useEffect(() => {
    dispatch(getCountriesUser());
    dispatch(getApprovalMatrixType());

    const statusDefault =
      getOptionsStatus().find(item => item.key === StatusRegisterEnum.Active) ??
      null;

    methods.setValue('approvalMatrixInformation.statusRegister', statusDefault);

    if (!isNullUndefinedEmpty(approvalMatrixGuid)) {
      load(approvalMatrixGuid);
    }
  }, [approvalMatrixGuid, dispatch]);

  const isReadyToMap =
    approvalMatrixData !== null &&
    !isLoadingCountries &&
    countries?.length !== 0 &&
    !isLoadingInsurers &&
    insurers?.length !== 0 &&
    !isLoadingTypes &&
    approvalMatrixTypes?.length !== 0 &&
    !isLoadingHierarchy &&
    userHierarchies?.length !== 0;

  useEffect(() => {
    if (!isReadyToMap || !isLoading) return;

    const formattedData: IApprovalMatrixRegisterForm = {
      approvalMatrixInformation: {
        country:
          countries?.find(
            c => Number(c.enum) === approvalMatrixData.idCountry
          ) ?? null,
        insurer:
          insurers?.find(
            i => String(i.key) === approvalMatrixData.guidInsurer
          ) ?? null,
        matrixName: approvalMatrixData.name,
        approvalMatrixType:
          approvalMatrixTypes?.find(
            t => Number(t.key) === approvalMatrixData.idApprovalMatrixType
          ) ?? null,
        statusRegister:
          getOptionsStatus().find(
            s => s.key === approvalMatrixData.idStatusRegister
          ) ?? null
      },
      approvalMatrixValues: approvalMatrixData.approvalMatrixValues.map(
        val => ({
          hierarchyLevel:
            userHierarchies?.find(h => Number(h.key) === val.hierarchyLevel) ||
            null,
          maximumValue: formatCurrency(val.maximumValue),
          active: val.active,
          guid: val.guid
        })
      )
    };

    methods.reset(formattedData);

    setIsLoading(false);
  }, [isReadyToMap, isLoading, methods]);

  if (isLoading) return <LoadingComponent />;

  return (
    <ContainerScreen
      title={`
      ${getTranslateByKey('information.registration')}
      ${' > '}
      ${getTranslateByKey('screen.approvalMatrixRegistration')}`}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
          <S.Container>
            <ApprovalMatrixInfo isEdit={!isNullUndefined(approvalMatrixData)} />
            <ApprovalMatrixValues />
            {!isNullUndefinedEmpty(approvalMatrixGuid) && (
              <ApprovalMatrixHistoric
                approvalMatrixGuid={approvalMatrixGuid}
                triggerReload={triggerReloadHistoric}
              />
            )}

            <S.Buttons>
              <SecondaryButton
                id="id-registerApprovalMatrix-btn-back"
                onClick={() => handleBack()}>
                {getTranslateByKey('information.back')}
              </SecondaryButton>
              <Button
                id="id-registerApprovalMatrix-btn-submit"
                type="submit"
                disabled={!methods.formState.isDirty}
                isLoading={isLoadingSave}>
                {getTranslateByKey('information.save')}
              </Button>
            </S.Buttons>
          </S.Container>
        </form>
      </FormProvider>
    </ContainerScreen>
  );
}
